import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api/client.js';
import { Alert, Button, Card, Field, Input, Spinner } from './ui.js';
import { PasswordChecklist, passwordMeetsPolicy } from './PasswordChecklist.js';

/**
 * The public page behind an invitation or reset link.
 *
 * The token in the address is the only credential, so this renders signed out and
 * before the session is consulted. It first checks the link (a 404 means invalid
 * or expired) without ever asking the server who it is for — the page shows no
 * email or name, only that a link is valid — then takes a new password. On
 * success it does a full navigation to the root so the session-driven shell picks
 * up where the server left the account: enrolling an authenticator for a fresh
 * invitation, or entering a code for a reset. The second factor is never skipped.
 */

type Kind = 'invitation' | 'password_reset';

const COPY: Record<
  Kind,
  { title: string; subtitle: string; submit: string; working: string; success: string }
> = {
  invitation: {
    title: 'Set up your account',
    subtitle: 'Choose a password. Next you will set up an authenticator app.',
    submit: 'Create account',
    working: 'Setting up…',
    success: 'Password set. Taking you to set up your authenticator…',
  },
  password_reset: {
    title: 'Choose a new password',
    subtitle: 'You will sign in with your authenticator afterwards.',
    submit: 'Set new password',
    working: 'Saving…',
    success: 'Password changed. Taking you to sign in with your authenticator…',
  },
};

/** The token from `/invite/<token>` or `/reset/<token>`. */
function tokenFromPath(): string {
  const parts = window.location.pathname.split('/').filter(Boolean);
  return parts.length >= 2 ? decodeURIComponent(parts[1]!) : '';
}

interface PeekResponse {
  purpose: Kind;
  expiresAt: string;
}

type Phase =
  { state: 'checking' } | { state: 'invalid' } | { state: 'ready' } | { state: 'accepted' };

export function TokenAcceptFlow({ kind }: { kind: Kind }) {
  const copy = COPY[kind];
  const [token] = useState(tokenFromPath);
  const [phase, setPhase] = useState<Phase>({ state: 'checking' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let canceled = false;
    if (!token) {
      setPhase({ state: 'invalid' });
      return;
    }
    api
      .get<PeekResponse>(`/api/auth/token/${encodeURIComponent(token)}`)
      .then((found) => {
        if (canceled) return;
        // A link opened at the wrong page (a reset link on /invite) is treated as
        // invalid rather than quietly doing the other thing.
        setPhase(found.purpose === kind ? { state: 'ready' } : { state: 'invalid' });
      })
      .catch(() => {
        if (!canceled) setPhase({ state: 'invalid' });
      });
    return () => {
      canceled = true;
    };
  }, [token, kind]);

  const mismatch = confirm.length > 0 && password !== confirm;
  const meetsPolicy = passwordMeetsPolicy(password);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api.post('/api/auth/token/accept', { token, password });
      setPhase({ state: 'accepted' });
      // A full navigation, not a client route: the signed-out shell only mounts
      // the enrollment or code screen after re-reading /api/auth/me.
      window.location.assign('/');
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        setPhase({ state: 'invalid' });
      } else {
        setError(caught instanceof ApiError ? caught.message : 'Could not set your password.');
      }
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-sunken px-4 py-12">
      <div className="w-full max-w-sm">
        <header className="mb-6 text-center">
          <h1 className="text-lg font-semibold tracking-tight text-strong">{copy.title}</h1>
          <p className="mt-1 text-sm text-muted">{copy.subtitle}</p>
        </header>

        <Card className="p-6">
          {phase.state === 'checking' && (
            <div className="py-6">
              <Spinner label="Checking your link" />
            </div>
          )}

          {phase.state === 'invalid' && (
            <div className="space-y-4">
              <Alert>
                This link is invalid or has expired. Ask an administrator to send a new invitation,
                or use “Forgot your password?” on the sign-in page.
              </Alert>
              <a
                href="/"
                className="block text-center text-sm text-brand-700 underline underline-offset-2"
              >
                Go to sign in
              </a>
            </div>
          )}

          {phase.state === 'accepted' && <Alert tone="success">{copy.success}</Alert>}

          {phase.state === 'ready' && (
            <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
              {error && <Alert>{error}</Alert>}

              <div>
                <Field label="New password">
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    autoFocus
                    disabled={busy}
                  />
                </Field>
                <PasswordChecklist password={password} />
              </div>

              <Field label="Confirm password">
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={busy}
                />
              </Field>

              {mismatch && (
                <p className="text-xs text-danger-text">The two passwords do not match.</p>
              )}

              <Button type="submit" className="w-full" disabled={busy || mismatch || !meetsPolicy}>
                {busy ? copy.working : copy.submit}
              </Button>
            </form>
          )}
        </Card>

        <p className="mt-4 text-center text-xs text-faint">
          Two-factor authentication is required on every account.
        </p>
      </div>
    </div>
  );
}
